import React, { useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';
import { isValidEmail } from '../util/email';
import { BOOKING_LINK } from '../config';

interface Props { navigate: (s: Screen) => void; user: User | null; }

// Locked pricing tiers, shown above the form. Kept as data so the table stays
// consistent on mobile and desktop.
const TIERS: { students: string; price: string }[] = [
  { students: 'Up to 300 students', price: '$6,000 / year' },
  { students: 'Up to 600 students', price: '$12,000 / year' },
  { students: 'Up to 900 students', price: '$18,000 / year' },
  { students: 'Up to 1,200 students', price: '$24,000 / year' },
  { students: 'More than 1,200 students', price: 'Contact us' },
];

// The student-count choices align with the pricing tiers so each lead maps to one.
const STUDENT_OPTIONS = ['Up to 300', 'Up to 600', 'Up to 900', 'Up to 1,200', 'More than 1,200'];

export default function Schools({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';

  const [schoolName, setSchoolName] = useState('');
  const [district, setDistrict] = useState('');
  const [stateName, setStateName] = useState('');
  const [studentCount, setStudentCount] = useState('');
  const [contactName, setContactName] = useState(user ? `${user.firstName} ${user.lastName}`.trim() : '');
  const [role, setRole] = useState('');
  const [workEmail, setWorkEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [situation, setSituation] = useState('');
  const [company, setCompany] = useState(''); // honeypot, real people leave it blank

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const hasBookingLink = !!BOOKING_LINK;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!schoolName.trim()) { setErr('Please enter your school name.'); return; }
    if (!contactName.trim()) { setErr('Please enter your name.'); return; }
    if (!isValidEmail(workEmail)) { setErr('Please enter a valid work email so we can reply.'); return; }
    setBusy(true);
    try {
      await authApi.requestLicense({
        schoolName: schoolName.trim(),
        district: district.trim(),
        state: stateName.trim(),
        studentCount: studentCount.trim(),
        contactName: contactName.trim(),
        role: role.trim(),
        workEmail: workEmail.trim(),
        phone: phone.trim(),
        situation: situation.trim(),
        ...(company ? ({ company } as any) : {}),
      });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (e: any) {
      setErr(e?.message || "We couldn't submit your request just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>School Licenses</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 28 }}>
        {sent ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>✅</p>
            <h2 style={{ marginBottom: 8 }}>Request received</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
              Thanks, {contactName.trim().split(/\s+/)[0] || 'there'}. We sent a confirmation to <strong>{workEmail.trim()}</strong>, and Sean will reply within one business day.
            </p>
            {hasBookingLink && (
              <a href={BOOKING_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none', width: 'auto', padding: '12px 22px', marginBottom: 12 }}>
                Book a call now
              </a>
            )}
            <div>
              <button onClick={() => navigate(home)} className="btn-secondary" style={{ width: 'auto', padding: '12px 22px' }}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 26, marginBottom: 8 }}>Get a school license</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              One invoice, full building access, fundable through Title IV-A.
            </p>

            {/* Locked pricing tiers */}
            <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: 'var(--teal-light)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--teal)', fontWeight: 700 }}>Building size</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px', color: 'var(--teal)', fontWeight: 700 }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIERS.map((t, i) => (
                      <tr key={t.students} style={{ borderTop: '1px solid #f0ece6', background: i % 2 ? 'transparent' : 'rgba(0,0,0,0.01)' }}>
                        <td style={{ padding: '12px 16px' }}>{t.students}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700 }}>{t.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Request form */}
            <div className="card">
              <form onSubmit={submit}>
                <div className="form-group">
                  <label>School name *</label>
                  <input type="text" value={schoolName} onChange={e => setSchoolName(e.target.value)} placeholder="Lincoln Elementary" />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>District</label>
                    <input type="text" value={district} onChange={e => setDistrict(e.target.value)} placeholder="Springfield SD" />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input type="text" value={stateName} onChange={e => setStateName(e.target.value)} placeholder="VT" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Approximate number of students</label>
                  <select value={studentCount} onChange={e => setStudentCount(e.target.value)}>
                    <option value="">Select a range</option>
                    {STUDENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Your name *</label>
                    <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="First and last name" />
                  </div>
                  <div className="form-group">
                    <label>Your role</label>
                    <input type="text" value={role} onChange={e => setRole(e.target.value)} placeholder="Principal, Director of Student Services" />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Work email *</label>
                    <input type="email" value={workEmail} onChange={e => setWorkEmail(e.target.value)} placeholder="you@school.org" />
                  </div>
                  <div className="form-group">
                    <label>Phone (optional)</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 555-5555" />
                  </div>
                </div>
                <div className="form-group">
                  <label>What is happening in your building right now? (optional)</label>
                  <textarea value={situation} onChange={e => setSituation(e.target.value)} placeholder="Tell us what you are seeing and what you are hoping to change." rows={5} style={{ resize: 'vertical' }} />
                </div>

                {/* Honeypot: hidden from people, tempting to bots. */}
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

                {err && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{err}</div>}
                <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Request a license'}</button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
